import React, { useMemo } from "react";
import { stepToEnglish, stepToScript } from "../stepEnglish";

export const NO_ARTIFACT_STEP = "No Artifact step found.";

/* The Gherkin line a Vision step implements: `bdd_step` from the step
   traceability, else `source_step_no`. 0 when neither is known. */
const lineOf = (s) => Number(s?.bdd_step || s?.source_step_no || 0);

function ArtifactStep({ s, n }) {
  const script = stepToScript(s);
  return (
    <li className="flex items-start gap-2 text-[12px] text-slate-700">
      <span className="mt-px flex h-4 min-w-[1rem] flex-none items-center justify-center rounded bg-brand-50 px-1 text-[9px] font-semibold text-brand-700">
        {n}
      </span>
      <span className="min-w-0">
        {stepToEnglish({ ...s, description: undefined })}
        {script && <span className="mt-0.5 block break-all font-mono text-[10px] text-slate-400">{script}</span>}
      </span>
    </li>
  );
}

/* An App Page Artifact beside its Test: each Cucumber line (left) with the
   Playwright steps that implement it (right), like the Create Artifact
   dialog on the Requirement Board. Steps the pipeline could not attribute to
   a line are listed last. */
export default function AppPageArtifactSteps({ lines, steps }) {
  const list = useMemo(() => (Array.isArray(steps) ? steps : []), [steps]);
  const byLine = useMemo(() => {
    const m = {};
    list.forEach((s) => {
      const n = lineOf(s);
      (m[n] = m[n] || []).push(s);
    });
    return m;
  }, [list]);
  const known = new Set((lines || []).map((l) => l.step_no));
  const unmapped = list.filter((s) => !known.has(lineOf(s)));
  let counter = 0;

  /* No step says which line it implements (scripts made before the step
     traceability existed): the scenario and the script side by side, rather
     than "No Artifact step found." on every line. */
  if (list.length && unmapped.length === list.length) {
    return (
      <div className="grid gap-3 md:grid-cols-2">
        <section className="rounded-lg border border-slate-200 bg-card p-3">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">Cucumber scenario</p>
          <ol className="space-y-1.5">
            {(lines || []).map((line) => (
              <li key={line.step_no} className="text-[12px] text-slate-800">
                <span className="mr-1.5 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">{line.step_no}</span>
                {line.keyword && <span className="mr-1 font-semibold text-brand-700">{line.keyword}</span>}
                {line.action}
                {line.data && (
                  <span className="mt-1 block whitespace-pre-wrap font-mono text-[10px] text-slate-500">{line.data}</span>
                )}
              </li>
            ))}
          </ol>
        </section>
        <section className="rounded-lg border border-slate-200 bg-card p-3">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">Playwright script</p>
          <ol className="space-y-1.5">
            {list.map((s, i) => (
              <ArtifactStep key={i} s={s} n={i + 1} />
            ))}
          </ol>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {(lines || []).map((line) => {
        const mapped = byLine[line.step_no] || [];
        return (
          <div key={line.step_no} className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-card p-2.5">
            <p className="text-[12px] text-slate-800">
              <span className="mr-1.5 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">
                {line.step_no}
              </span>
              {line.keyword && <span className="mr-1 font-semibold text-brand-700">{line.keyword}</span>}
              {line.action}
              {line.data && (
                <span className="mt-1 block whitespace-pre-wrap font-mono text-[10px] text-slate-500">{line.data}</span>
              )}
            </p>
            <ol className="space-y-1.5">
              {mapped.length ? (
                mapped.map((s, i) => <ArtifactStep key={i} s={s} n={++counter} />)
              ) : (
                <li className="text-[12px] italic text-slate-400">{NO_ARTIFACT_STEP}</li>
              )}
            </ol>
          </div>
        );
      })}
      {unmapped.length > 0 && (
        <div className="grid grid-cols-2 gap-3 rounded-lg border border-dashed border-slate-200 p-2.5">
          <p className="text-[11px] text-slate-400">Additional steps</p>
          <ol className="space-y-1.5">
            {unmapped.map((s, i) => (
              <ArtifactStep key={i} s={s} n={++counter} />
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
